import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/cn';

const buttonVariants = cva(
  `inline-flex items-center justify-center
   text-sm font-normal rounded-md
   transition-colors cursor-pointer`,
  {
    variants: {
      size: {
        default: 'h-[2.25rem] px-3 gap-2',
      },

      variant: {
        default: `
          border border-transparent
          hover:text-primary-active
          active:border-primary-active active:text-primary-active
          text-primary
        `,
        outline: `
          border border-outline
          hover:border-outline hover:text-primary-active
          active:border-primary-active active:text-primary-active
          text-primary
        `,
        ghost: `
          border-0 p-0 h-auto font-normal
          text-text-link hover:underline
          disabled:opacity-50 disabled:cursor-not-allowed
        `,
      },

      disabledState: {
        true: 'opacity-50 pointer-events-none cursor-not-allowed',
        false: '',
      },
    },

    defaultVariants: {
      size: 'default',
      variant: 'default',
      disabledState: false,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  iconImg?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, asChild = false, iconImg, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const hasIcon = Boolean(iconImg);

    return (
      <Comp
        ref={ref}
        disabled={disabled}
        className={cn(
          buttonVariants({
            variant,
            disabledState: disabled,
            className,
          }),
        )}
        {...props}
      >
        {hasIcon && <span className="flex items-center">{iconImg}</span>}
        {children}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button };
